import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../store";
import {
  PENDING_LAUNCH_INTENT_EVENT,
  capturePlatformLaunchIntent,
  clearPendingLaunchIntent,
  launchIntentRoute,
  readPendingLaunchIntent,
  type LaunchIntent,
} from "../lib/launchIntent";
import { getPlatformAdapter } from "../lib/platform";
import { isSupabaseConfigured } from "../lib/supabase";
import { TELEGRAM_BOT_USERNAME } from "../lib/telegramAuth";

/**
 * Keeps provider launch navigation behind consent and onboarding.
 *
 * Telegram capture is triggered by TelegramConsentGate after the consent
 * decision. Other hosts have no such gate, so they are captured here.
 */
export function LaunchIntentBoundary() {
  const { state } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const initialPending = readPendingLaunchIntent();
  const [pending, setPending] = useState<LaunchIntent | null>(initialPending);
  const [activation, setActivation] = useState(initialPending ? 1 : 0);
  const handledActivation = useRef(0);
  const wasOnboarded = useRef(state.settings.onboardingCompleted);

  useEffect(() => {
    const handlePending = (event: Event) => {
      const intent = (event as CustomEvent<LaunchIntent | null>).detail;
      setPending(intent);
      if (intent) setActivation((value) => value + 1);
    };
    window.addEventListener(PENDING_LAUNCH_INTENT_EVENT, handlePending);

    const adapter = getPlatformAdapter();
    const telegramConsentGateHandlesLaunch =
      adapter.platform === "telegram" &&
      isSupabaseConfigured &&
      Boolean(TELEGRAM_BOT_USERNAME);
    if (!telegramConsentGateHandlesLaunch) {
      capturePlatformLaunchIntent();
    }

    return () => window.removeEventListener(PENDING_LAUNCH_INTENT_EVENT, handlePending);
  }, []);

  useEffect(() => {
    const onboardingCompleted = state.settings.onboardingCompleted;
    const justCompleted = !wasOnboarded.current && onboardingCompleted;
    wasOnboarded.current = onboardingCompleted;

    if (!pending) return;

    const newlyActivated = activation > handledActivation.current;
    if (newlyActivated) handledActivation.current = activation;
    const target = launchIntentRoute(pending);

    if (!onboardingCompleted) {
      // Invitations and shared learning objects show their public preview
      // before onboarding/auth. The user chooses whether to keep them.
      if (pending.kind === "invite" || pending.kind === "growth-share") {
        if (location.pathname !== target && newlyActivated) navigate(target, { replace: true });
        return;
      }
      if (location.pathname !== "/onboarding") navigate("/onboarding", { replace: true });
      return;
    }

    if (
      newlyActivated ||
      justCompleted
    ) {
      if (pending.kind !== "invite" && pending.kind !== "growth-share") {
        clearPendingLaunchIntent(pending);
      }
      if (location.pathname !== target) {
        navigate(target, {
          replace: true,
          state: pending.kind === "drill" ? { source: "startapp" } : undefined,
        });
      }
    }
  }, [
    activation,
    location.pathname,
    navigate,
    pending,
    state.settings.onboardingCompleted,
  ]);

  return <Outlet />;
}
